import { useCallback, useRef, useState } from "react";
import {
  Layout,
  TabNode,
  TabSetNode,
  BorderNode,
  type ITabSetRenderValues,
  type ITabRenderValues,
} from "flexlayout-react";
import { debounce } from "lodash";
import { message } from "antd";
import { useTranslation } from "react-i18next";

import {
  useEditorState
} from "@/store";
import { GodotFrame } from "./GodotFrame";
import { ComplementaryFrame } from "./ComplementaryFrame";
import { MonacoEditor } from "./Monaco/MonacoEditor";
import { Documentation } from "./Documentation";
import { Achievement } from "./Achievement";
import { QA } from "./QA";
import { postMessageToRef, Logger } from "@/utils";
import { downloadBlocklyScreenshot } from "@/utils/blockly-screenshot";

import { BlocklyEditor } from ".";
import { FSViewer, JsonViewer, LayoutTest, LogStore } from "./Borders";
import { FloatMenu } from "./FloatMenu";
import { BlocklyScreenshot } from "./shared";


function App() {
  const { godotContainerRef, parseAchievementId, parseQAId } = useEditorState();

  const [messageApi, contextHolder] = message.useMessage();
  const { t } = useTranslation();
  const [downloadingScreenshot, setDownloadingScreenshot] = useState(false);

  const showMessage = useCallback((type: string, message: string) => {
    if (type === "error") {
      messageApi.error({ content: t(message), duration: 6, key: "blockly-error" });
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const readyDebouncer = useCallback(
    debounce(() => {
      postMessageToRef(godotContainerRef, {
        action: "setEditorLayoutResponse",
        status: "layoutInitialized",
      });
    }, 500),
    [godotContainerRef]
  );

  const screenshotButtonRef = useRef<HTMLButtonElement>(null);

  const handleScreenshotDownload = async () => {
    try {
      await downloadBlocklyScreenshot((loading) => setDownloadingScreenshot(loading));
      message.success(t("screenshot_downloaded_success"), 2);
    } catch (error: any) {
      Logger.error("Screenshot error:", error);
      if (error.message === "No blocks in workspace") {
        message.warning(t("screenshot_no_blocks"), 2);
      } else {
        message.error(t("screenshot_download_error"), 2);
      }
    }
  };

  const factory = (node: TabNode) => {
    const { blocklyRefreshKey } = useEditorState.getState();

    const component = node.getComponent();
    switch (component) {
      case "placeholder":
        return <div className="placeholder">{node.getName()}</div>;
      case "json":
        return <JsonViewer json={node.getConfig()?.json || JSON.parse(useEditorState.getState().toJson())} />;
      case "fsViewer":
        return <FSViewer />;
      case "logStore":
        return <LogStore />;
      case "modelEditor":
        return <LayoutTest />;
      case "Simulation": {
        const project_id = useEditorState.getState().project?.project_id || "";
        if (project_id.startsWith("complementary;")) {
          return <ComplementaryFrame projectId={project_id} />;
        }
        return <GodotFrame module={node.getConfig()?.module} />;
      }
      case "MiniBrowser":
        return <Documentation src={node.getConfig().src} id={node.getId()} />;
      case "Achievement":
        return (
          <Achievement
            achievement_id={parseAchievementId(node.getConfig().achievement_id)}
          />
        );
      case "Editor":
        return (
          <MonacoEditor
            key={blocklyRefreshKey}
            file_path={node.getConfig().file_path as string}
            setMount={() => { }}
            initialCode={node.getConfig().initialCode as string | undefined}
            readonly={node.getConfig().readonly as boolean}
          />
        );
      case "Blockly":
        return (
          <BlocklyEditor
            key={blocklyRefreshKey}
            file_path={node.getConfig().file_path as string}
            xml_path={node.getConfig().xml_file_path as string}
            initialXML={node.getConfig().initialXML as string | undefined}
            plugins={node.getConfig().plugins as string[]}
            renderer={
              node.getConfig().renderer as "zelos" | "geras" | "thrasos"
            }
            config={node.getConfig().config}
            showMessage={showMessage}
          />
          // continuous-toolbox
          // content-highlight
          // suggested-blocks
        );
      case "QA":
        return <QA quizId={parseQAId(node.getConfig().quizId)} />;
      default:
        return <div></div>;
    }
  };

  const onRenderTabSet = (
    node: TabSetNode | BorderNode,
    renderValues: ITabSetRenderValues
  ) => {
    if (node instanceof TabSetNode) {
      // Check if this tabset contains a Blockly tab
      const hasBlocklyTab = node.getChildren().some((child) => {
        if (child instanceof TabNode) {
          return child.getComponent() === "Blockly";
        }
        return false;
      });

      if (hasBlocklyTab) {
        const rect = node.getRect();
        // Check if the tabset width is small (e.g. < 400px) to hide text
        // This ensures the button doesn't break layout on small screens
        const isSmall = rect.width < 480;

        renderValues.stickyButtons.push(
          <button
            ref={screenshotButtonRef}
            key="screenshot-btn"
            className="flexlayout__tab_toolbar_button space-x-2 text-gray-500 hover:text-gray-700 hover:!bg-transparent !bg-transparent"
            title={t("downloadBlocklyScreenshot")}
            onClick={handleScreenshotDownload}
            disabled={downloadingScreenshot}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: downloadingScreenshot ? 0.5 : 1,
              cursor: downloadingScreenshot ? "not-allowed" : "pointer"
            }}
          >
            {!isSmall && <span>{t("blocklyScreenshot")}</span>}
            <BlocklyScreenshot size={17} />
          </button>
        );
      }
    }
    readyDebouncer();
  };

  const onRenderTab = (
    node: TabNode,
    renderValues: ITabRenderValues
  ) => {
    const component = node.getComponent();
    if (component === "Blockly") {
      renderValues.leading = <img
        src="https://editor.riders.ai/icons/blockly.svg"
        alt="blockly"
      />;
    } else if (component === "Editor") {
      renderValues.leading = <img
        src="https://editor.riders.ai/icons/preview.svg"
        alt="preview"
      />
    } else {
      const config = node.getConfig();
      if (config?.tabIcon) {
        renderValues.leading = <img
          src={config.tabIcon}
          alt="icon"
        />;
      }
    }
  };

  const onModelChange = debounce(() => {
    const appState = useEditorState.getState();

    const rowNode = appState.layout.model.getNodeById("main-row");
    const weights = rowNode?.getChildren().map(child => (child as TabSetNode).getWeight());

    const prevCodeTabWidth = appState.codeTabWidth;
    const prevCodeTabHeight = appState.codeTabHeight;

    if (weights?.length === 2) {
      appState.setCodeTabWidth(weights[0]);
    }

    const parent = appState.layout.model.getNodeById("secondary-mini-browser-tab")?.getParent() as TabSetNode;
    if (parent) {
      const weights = parent.getWeight()
      appState.setCodeTabHeight(100 - weights);
    }

    if (useEditorState.getState().codeTabHeight !== prevCodeTabHeight || useEditorState.getState().codeTabWidth !== prevCodeTabWidth) {
      if (appState.saveStatus !== "SAVING") {
        useEditorState.setState({ saveStatus: "NEEDSAVE" });
      }
    }
  },
    200
  );

  const project_id = useEditorState.getState().project?.project_id || "";
  const isComplementary = project_id.startsWith("complementary;");

  if (isComplementary) {
    return (
      <div className="w-full h-full relative">
        <ComplementaryFrame projectId={project_id} />
        <FloatMenu showModal={() => { }} clickAndCloseHandler={() => { }} />
        {contextHolder}
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <Layout
        model={useEditorState.getState().layout.model}
        factory={factory}
        onRenderTabSet={onRenderTabSet}
        onRenderTab={onRenderTab}
        realtimeResize={true}
        onModelChange={onModelChange}
      />
      <FloatMenu showModal={() => { }} clickAndCloseHandler={() => { }} />
      {contextHolder}
    </div>
  );
}

export default App;
